/**
 * The inventory page's filters as URL state (Phase C1). The URL holds the whole state, so a link
 * or a saved view reproduces the list exactly.
 *
 * Multi-selects are comma-separated (`condition=Near Mint,Lightly Played`); values containing a
 * comma cannot be expressed and are not offered. `q` is the search box as typed — it may still
 * contain tokens the user has not turned into chips yet (`game:pokemon`), which the list request
 * handles with the same parser as the chips (see inventoryQueryTokens).
 */
import {
  INVENTORY_CHANNELS,
  INVENTORY_KINDS,
  INVENTORY_LISTING_STATUSES,
  INVENTORY_SYNC_STATES,
  gameMatchKeys,
  resolveNamedValues,
  setMatchKeys,
  type InventoryChannel,
  type InventoryKind,
  type InventoryListingStatus,
  type InventoryQueryToken,
  type InventorySyncState,
} from './inventoryQueryTokens'
import type { CardSet, Game } from './inventoryTypes'

export type InventoryStockParam = '' | 'soldout' | 'all'
export type InventoryPreReleaseParam = '' | 'any' | 'draft' | 'preorder'
export type InventoryAddedParam = '' | '7' | '30'
export type InventorySoldParam = '' | '7' | '30' | '90'

export type InventoryUrlFilters = {
  games: string[]
  sets: string[]
  conditions: string[]
  printings: string[]
  kinds: InventoryKind[]
  categories: string[]
  /** Storefront tags as typed (Phase C4); matched case-insensitively by the server. */
  tags: string[]
  channels: InventoryChannel[]
  statuses: InventoryListingStatus[]
  sync: InventorySyncState[]
  consigned: boolean
  attention: boolean
  added: InventoryAddedParam
  /** Sold at least one unit in the last N days. */
  sold: InventorySoldParam
  /** '' = in stock, the default, kept out of the URL. */
  stock: InventoryStockParam
  onSale: boolean
  manual: boolean
  lowStock: boolean
  preRelease: InventoryPreReleaseParam
  /** Price bounds as typed, in the display currency. */
  minCost: string
  maxCost: string
}

/** The multi-select filters and their URL keys. */
export const LIST_KEYS = {
  games: 'game',
  sets: 'set',
  conditions: 'condition',
  printings: 'printing',
  kinds: 'kind',
  categories: 'category',
  tags: 'tag',
  channels: 'channels',
  statuses: 'status',
  sync: 'sync',
} as const
export type InventoryListFilterKey = keyof typeof LIST_KEYS

/**
 * Keys "Clear" removes: every filter and the search box. `sort` (orders, does not narrow),
 * `limit` and the browse-by-set toggle are kept. `number` and `filters` are retired keys from
 * the old two-box / dropdown-panel page, cleared so old links do not leave them behind.
 */
export const INVENTORY_FILTER_URL_KEYS_TO_CLEAR = [
  'q', 'number', ...Object.values(LIST_KEYS), 'consigned', 'attention', 'added', 'sold', 'stock', 'onSale', 'onlyOnSale',
  'manual', 'lowStock', 'preRelease', 'minCost', 'maxCost', 'filters', 'view',
] as const

export function readList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key)
  if (!raw) return []
  return [...new Set(raw.split(',').map((part) => part.trim()).filter(Boolean))]
}

export function writeList(params: URLSearchParams, key: string, values: readonly string[]): void {
  const clean = [...new Set(values.map((value) => value.trim()).filter((value) => value && !value.includes(',')))]
  if (clean.length > 0) params.set(key, clean.join(','))
  else params.delete(key)
}

const only = <T extends string>(allowed: readonly T[], values: string[]): T[] =>
  values.filter((value): value is T => (allowed as readonly string[]).includes(value))

export function parseInventoryUrlFilters(params: URLSearchParams): InventoryUrlFilters {
  const stock = params.get('stock')
  const preRelease = params.get('preRelease')
  const added = params.get('added')
  const sold = params.get('sold')
  const legacyOnSale = params.get('onlyOnSale')
  return {
    games: readList(params, LIST_KEYS.games),
    sets: readList(params, LIST_KEYS.sets),
    conditions: readList(params, LIST_KEYS.conditions),
    printings: readList(params, LIST_KEYS.printings),
    kinds: only(INVENTORY_KINDS, readList(params, LIST_KEYS.kinds)),
    categories: readList(params, LIST_KEYS.categories),
    tags: readList(params, LIST_KEYS.tags),
    channels: only(INVENTORY_CHANNELS, readList(params, LIST_KEYS.channels)),
    statuses: only(INVENTORY_LISTING_STATUSES, readList(params, LIST_KEYS.statuses)),
    sync: only(INVENTORY_SYNC_STATES, readList(params, LIST_KEYS.sync)),
    consigned: params.get('consigned') === '1',
    attention: params.get('attention') === '1',
    added: added === '7' || added === '30' ? added : '',
    sold: sold === '7' || sold === '30' || sold === '90' ? sold : '',
    stock: stock === 'soldout' || stock === 'all' ? stock : '',
    onSale: params.get('onSale') === '1' || legacyOnSale === '1' || legacyOnSale === 'true',
    manual: params.get('manual') === '1',
    lowStock: params.get('lowStock') === '1',
    preRelease: preRelease === 'any' || preRelease === 'draft' || preRelease === 'preorder' ? preRelease : '',
    minCost: params.get('minCost') ?? '',
    maxCost: params.get('maxCost') ?? '',
  }
}

/**
 * How many filters are on, for the badge and "Clear". Each multi-select counts once however many
 * values it has, and a price range is one filter whichever ends are set. The search box is not
 * counted: it is visible in its own input.
 */
export function countActiveInventoryFilters(f: InventoryUrlFilters): number {
  return [
    ...Object.keys(LIST_KEYS).map((key) => f[key as InventoryListFilterKey].length > 0),
    f.consigned, f.attention, f.added, f.sold, f.stock, f.onSale, f.manual, f.lowStock, f.preRelease,
    f.minCost.trim() || f.maxCost.trim(),
  ].filter(Boolean).length
}

export function clearInventoryFilterParams(p: URLSearchParams): void {
  for (const key of INVENTORY_FILTER_URL_KEYS_TO_CLEAR) p.delete(key)
  p.set('page', '1')
}

/**
 * Old links: the retired set-number box (`number=`) joins the one search box, and the legacy
 * `onlyOnSale` flag becomes `onSale=1`. Returns whether `p` changed.
 */
export function migrateLegacyInventoryParams(p: URLSearchParams): boolean {
  let changed = false
  const number = p.get('number')
  if (number !== null) {
    const q = [p.get('q') ?? '', number].map((part) => part.trim()).filter(Boolean).join(' ')
    if (q) p.set('q', q)
    p.delete('number')
    changed = true
  }
  if (p.has('onlyOnSale')) {
    const legacy = p.get('onlyOnSale')
    p.delete('onlyOnSale')
    if (legacy === '1' || legacy === 'true') p.set('onSale', '1')
    changed = true
  }
  if (p.has('filters')) {
    p.delete('filters')
    changed = true
  }
  return changed
}

export type InventoryTokenCatalog = { games: readonly Game[]; sets: readonly CardSet[] }

/**
 * Typed tokens → URL filters (what Enter in the search box does). Returns the text left for the
 * box: the free words plus any token that could not become a chip — a `game:`/`set:` naming
 * nothing in this store's picklists stays as text, and the list endpoint still resolves it.
 */
export function applyTokensToParams(
  p: URLSearchParams,
  text: string,
  tokens: readonly InventoryQueryToken[],
  catalog: InventoryTokenCatalog,
): string {
  const leftover: string[] = text ? [text] : []
  const add = (key: string, values: readonly string[]) => writeList(p, key, [...readList(p, key), ...values])
  for (const token of tokens) {
    switch (token.key) {
      case 'game': {
        const ids = resolveNamedValues(token.values, catalog.games, gameMatchKeys).map((game) => game.id)
        if (ids.length > 0) add(LIST_KEYS.games, ids)
        else leftover.push(token.raw)
        break
      }
      case 'set': {
        const ids = resolveNamedValues(token.values, catalog.sets, setMatchKeys).map((set) => set.id)
        if (ids.length > 0) add(LIST_KEYS.sets, ids)
        else leftover.push(token.raw)
        break
      }
      case 'condition': add(LIST_KEYS.conditions, token.values); break
      case 'printing': add(LIST_KEYS.printings, token.values); break
      case 'kind': add(LIST_KEYS.kinds, token.values); break
      case 'tag': add(LIST_KEYS.tags, token.values); break
      case 'channel': add(LIST_KEYS.channels, token.values); break
      case 'status': add(LIST_KEYS.statuses, token.values); break
      case 'sync': add(LIST_KEYS.sync, token.values); break
      case 'stock':
        if (token.values[0] === 'in') p.delete('stock')
        else p.set('stock', token.values[0])
        break
      case 'minPrice': p.set('minCost', token.values[0]); break
      case 'maxPrice': p.set('maxCost', token.values[0]); break
    }
  }
  return leftover.join(' ')
}

/** Replace every filter (and the search box) with a saved view's; paging restarts. */
export function applyViewQuery(p: URLSearchParams, viewQuery: string): void {
  clearInventoryFilterParams(p)
  p.delete('sort')
  p.delete('browse')
  const view = new URLSearchParams(viewQuery)
  view.forEach((value, key) => {
    if (key !== 'page' && key !== 'limit') p.set(key, value)
  })
  p.set('page', '1')
}

/** The current filters, search and sort as a view's query string (paging left out). */
export function viewQueryFromParams(p: URLSearchParams): string {
  const out = new URLSearchParams()
  for (const key of [...INVENTORY_FILTER_URL_KEYS_TO_CLEAR, 'sort', 'browse']) {
    const value = p.get(key)
    if (value) out.set(key, value)
  }
  out.delete('filters')
  out.delete('view')
  return out.toString()
}
